import { AccessControl } from '@components/layout'
import {
  CalendarClock,
  Check,
  Copy,
  CreditCard,
  Download,
  FileText,
  Hash,
  Landmark
} from 'lucide-react'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RentModel } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { downloadPayBySquareQR } from '../utils/payBySquare'
import PayBySquareQR from './PayBySquareQR'

interface RentPaymentProps {
  rent: RentModel
  index?: number
  /** Render without the surrounding Card (e.g. inside a dialog). */
  embedded?: boolean
}

function InfoRow({
  icon: Icon,
  label,
  children
}: {
  icon: typeof Hash
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span className="shrink-0 text-muted-foreground">{label}:</span>
      <span className="min-w-0 font-medium text-ink">{children}</span>
    </div>
  )
}

const RentPayment = ({ rent, embedded }: RentPaymentProps) => {
  const { t } = useTranslation('contract')
  const [copied, setCopied] = useState(false)
  const payBySquare = rent.pay_by_square ?? ''

  const copyIban = async () => {
    if (!rent.bank_account) return
    await navigator.clipboard.writeText(rent.bank_account)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const title =
    rent?.payment_type === 'RENT' || rent?.payment_type === 'LANDLORD_RENT'
      ? rent?.variable_symbol?.startsWith('10') ||
        rent?.variable_symbol?.startsWith('11')
        ? t('rent_table.rent_partial')
        : t('rent_table.rent')
      : t('rent_table.deposit')

  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CreditCard className="h-5 w-5" />
          </span>
          <span className="font-display text-base font-bold text-ink">
            {title}
          </span>
        </div>
        <span className="inline-flex items-center rounded-full bg-peach/30 px-3 py-1 text-xs font-semibold text-warning">
          {t('rentWidget.unpaidTab', { ns: 'offers' })}
        </span>
      </div>

      <div className="mt-5 flex flex-col gap-6 @xl:flex-row @xl:items-start">
        {/* QR */}
        {payBySquare && (
          <div className="flex shrink-0 flex-col items-center gap-3">
            <div className="overflow-hidden rounded-2xl border-2 border-primary/60 bg-white p-1.5">
              <PayBySquareQR value={payBySquare} className="block" />
            </div>
            <Button
              variant="ghostDark"
              size="sm"
              onClick={() =>
                downloadPayBySquareQR(payBySquare, `qr-${rent.id}.png`)
              }
            >
              <Download className="h-4 w-4" />
              {t('rent_table.downloadQRCode')}
            </Button>
          </div>
        )}

        {/* Details */}
        <div className="flex-1 space-y-3.5">
          <div>
            <div className="text-xs font-medium text-muted-foreground">
              {t('rent_table.payment_information.amount')}
            </div>
            <div className="font-display text-3xl font-bold leading-none text-ink">
              {Number(rent.amount)?.toFixed(2)} €
            </div>
            {rent.payment_type === 'RENT' && (
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>
                  {t('rent_table.popOver.rent')}{' '}
                  <strong className="font-semibold text-ink/70">
                    {rent.rent} €
                  </strong>
                </span>
                <span>
                  {t('rent_table.popOver.monthly_advance_prescription')}{' '}
                  <strong className="font-semibold text-ink/70">
                    {rent.monthly_advance_prescription} €
                  </strong>
                </span>
                {(rent.fee ?? 0) > 0 && (
                  <span>
                    {t('rent_table.popOver.fee')}{' '}
                    <strong className="font-semibold text-ink/70">
                      {rent.fee} €
                    </strong>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="h-px w-full bg-ink/[0.06]" />

          <InfoRow icon={CalendarClock} label={t('rent_table.payment_information.due_date')}>
            {new Date(rent.due_date).toLocaleDateString('sk-SK')}
          </InfoRow>
          <InfoRow icon={Landmark} label={t('rent_table.payment_information.iban')}>
            <button
              type="button"
              onClick={copyIban}
              className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 font-medium text-ink hover:text-primary"
            >
              {rent.bank_account}
              {copied ? (
                <Check className="h-3.5 w-3.5 text-primary" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-primary" />
              )}
            </button>
          </InfoRow>
          <InfoRow icon={Hash} label={t('rent_table.payment_information.variable_symbol')}>
            {rent.variable_symbol}
          </InfoRow>
          <InfoRow icon={Hash} label={t('rent_table.payment_information.specific_symbol')}>
            {rent.specific_symbol || '-'}
          </InfoRow>
          <AccessControl role="ADMIN">
            <InfoRow icon={FileText} label={t('rent_table.payment_information.description')}>
              {rent.description || '-'}
            </InfoRow>
          </AccessControl>
        </div>
      </div>
    </>
  )

  return embedded ? (
    <div className="@container">{body}</div>
  ) : (
    <Card className="@container p-5 sm:p-6">{body}</Card>
  )
}

export default RentPayment
